import { DateFormatter } from '@internationalized/date';
import { format, formatDistanceToNowStrict, isAfter, isPast, parseISO } from 'date-fns';
import { parseDate } from 'utils/DateFormats';

/** Relative time from an ISO date string (e.g. "10 days ago"). */
export function formatRelativeDate(isoDate: string): string {
  return formatDistanceToNowStrict(new Date(isoDate), { addSuffix: true });
}

/**
 * Compact trip date range for overview UI (e.g. "3 - 10 Jun 26" or "28 Apr - 5 May 26").
 * Expects ISO date strings (yyyy-MM-dd).
 */
export function formatTripDateRange(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '';
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  return sameMonth
    ? `${format(start, 'd')} - ${format(end, 'd MMM yy')}`
    : `${format(start, 'd MMM')} - ${format(end, 'd MMM yy')}`;
}

export const formatExactDate = (
  startDate: string,
  endDate: string,
  locale: string | undefined,
  includeYear: boolean = true,
  compactDash: boolean = false
) => {
  const formatter = new DateFormatter(locale ?? 'en-GB', {
    year: includeYear ? '2-digit' : undefined,
    month: 'short',
    day: 'numeric'
  });

  // Parse dates as local dates to avoid timezone conversion issues
  const parseLocalDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  const formattedStartDate = formatter.formatToParts(parseLocalDate(startDate));
  const formattedEndDate = formatter.formatToParts(parseLocalDate(endDate));

  // Find the correct parts by their type
  const startDay = formattedStartDate.find((part) => part.type === 'day')?.value;
  const endDay = formattedEndDate.find((part) => part.type === 'day')?.value;
  const startMonth = formattedStartDate.find((part) => part.type === 'month')?.value;
  const endMonth = formattedEndDate.find((part) => part.type === 'month')?.value;
  const startYear = includeYear ? formattedStartDate.find((part) => part.type === 'year')?.value : undefined;
  const endYear = includeYear ? formattedEndDate.find((part) => part.type === 'year')?.value : undefined;

  const dash = compactDash ? '-' : ' - ';

  if (startMonth === endMonth) {
    return includeYear
      ? `${startDay}${dash}${endDay} ${startMonth} ${startYear}`
      : `${startDay}${dash}${endDay} ${startMonth}`;
  } else if (startMonth !== endMonth && startYear === endYear) {
    return includeYear
      ? `${startDay} ${startMonth}${dash}${endDay} ${endMonth} ${startYear}`
      : `${startDay} ${startMonth}${dash}${endDay} ${endMonth}`;
  } else {
    return includeYear
      ? `${startDay} ${startMonth} ${startYear}${dash}${endDay} ${endMonth} ${endYear}`
      : `${startDay} ${startMonth}${dash}${endDay} ${endMonth}`;
  }
};

// client or server-side compatible
export const getLowSeasonDefaultStartDate = (currentDate: Date = new Date()): Date => {
  // Handle null, invalid dates, or non-Date objects by using current date
  if (!currentDate || !(currentDate instanceof Date) || Number.isNaN(currentDate.getTime())) {
    currentDate = new Date();
  }

  const currentMonth = currentDate.getMonth() + 1; // Convert to 1-12 based
  const currentYear = currentDate.getFullYear();

  let targetMonth: number;
  let targetYear: number;

  if (currentMonth === 12) {
    // December
    targetMonth = 11; // November
    targetYear = currentYear + 1;
  } else if (currentMonth >= 9 && currentMonth <= 11) {
    // sep - nov
    targetMonth = 1; // January
    targetYear = currentYear + 1;
  } else {
    // January through July
    targetMonth = 11; // November
    targetYear = currentYear;
  }

  // Create first day of target month
  const firstDay = new Date(targetYear, targetMonth - 1, 1);

  // Calculate days until first Wednesday (3 = Wednesday, firstDay.getDay() returns 0-6)
  const daysUntilWed = (3 - firstDay.getDay() + 7) % 7;

  // Add 7 to get to second Wednesday
  const secondWed = new Date(targetYear, targetMonth - 1, 1 + daysUntilWed + 7);

  return secondWed;
};

/**
 * Returns the default start date if the provided date is in the past,
 * otherwise returns the original date.
 * Useful for ensuring dates are never in the past when displaying or using them.
 *
 * @param date - The date to check (string in 'yyyy-MM-dd' format or Date object)
 * @returns The default low-season start date if the date is in the past, otherwise the original date
 */
export const ensureFutureDate = (date: string | Date | null | undefined): Date => {
  const defaultStartDate = getLowSeasonDefaultStartDate();

  if (!date) {
    return defaultStartDate;
  }

  const dateToCheck = typeof date === 'string' ? parseDate(date) : date;

  if (isPast(dateToCheck)) {
    return defaultStartDate;
  }

  return dateToCheck;
};

/**
 * Returns the default start date as a string (yyyy-MM-dd) if the provided date is in the past,
 * otherwise returns the original date string.
 * Useful for ensuring date strings are never in the past when sending to APIs.
 *
 * @param date - The date string to check (in 'yyyy-MM-dd' format)
 * @returns The default low-season start date as string if the date is in the past, otherwise the original date string
 */
export const ensureFutureDateString = (date: string | null | undefined): string => {
  const defaultStartDate = getLowSeasonDefaultStartDate().toISOString().slice(0, 10);

  if (!date) {
    return defaultStartDate;
  }

  if (isPast(parseDate(date))) {
    return defaultStartDate;
  }

  return date;
};

/**
 * expirationDateTime parsed as Europe/London (BST/GMT aware)
 * Blueprints do not expire
 **/
export const isJourneyExpired = (expirationDateTime: Date | null | undefined, isBlueprint: boolean): boolean => {
  if (isBlueprint || !expirationDateTime) return false;
  return isAfter(new Date(), expirationDateTime);
};
